import type { Icon } from '@change/ds-icons';

import { Button, Text } from '@change/ds-core-ui/next';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { Translate, useI18n } from '@change-corgi/core/react/i18n';
import { Icon as LegacyIcon } from '@change-corgi/design-system-legacy/components/icon';
import type { IconComponent } from '@change-corgi/design-system-legacy/icons';
import type { ColorName } from '@change-corgi/design-system-legacy/theme';

import { useTrackView } from 'src/app/shared/hooks/tracking';

type InsightBoxParams = {
	topChannel: {
		channel: string;
		iconColor: ColorName | (string & {});
		signaturePercent: number;
		translationKey: TranslationKey;
		icon: React.FC<Icon> | IconComponent;
		isLegacyIcon?: boolean;
	};
	promoteLink: string;
	handlePromoteClick: () => void;
	handleShareClick: () => Promise<void>;
};

export function InsightBox({
	topChannel,
	promoteLink,
	handlePromoteClick,
	handleShareClick,
}: InsightBoxParams): React.JSX.Element {
	const { translate } = useI18n();
	useTrackView('petition_manage_signature_insights_box_view', { topChannel: topChannel.channel });
	const ChannelIcon = topChannel.icon;
	return (
		<div
			className="bg-background-overlay flex flex-col flex-nowrap items-center gap-6 rounded-lg p-6 text-center md:flex-row md:text-left"
			data-qa="signature-counts-insight-top-channel"
			data-testid="signature-counts-insight-message"
		>
			{topChannel.isLegacyIcon ? (
				<LegacyIcon
					data-testid="legacy-icon"
					// This type assertion is safe because we check isLegacyIcon before using the legacy icon component
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-type-assertion
					icon={topChannel.icon as IconComponent}
					size={40}
					color={topChannel.iconColor}
				/>
			) : (
				<ChannelIcon size={40} className={topChannel.iconColor} />
			)}
			{topChannel.channel === 'promotion' ? (
				<>
					<Text size="medium" fontWeight="light">
						<Translate value="corgi.petition.starter_dashboard_v2.petition_insights.signature_sources.insights.promote" />
					</Text>
					<Button
						data-testid="signature-source-share-petition-button"
						data-qa="signature-source-share-petition-button"
						href={promoteLink}
						as="a"
						onClick={handlePromoteClick}
						variant="secondary-solid"
						size="x-small"
					>
						<Translate value="corgi.petition.starter_dashboard_v2.petition_insights.signature_sources.promote_petition_button" />
					</Button>
				</>
			) : (
				<>
					<Text size="medium" fontWeight="light">
						<Translate
							value="corgi.petition.starter_dashboard_v2.petition_insights.signature_sources.insights.share"
							replacements={{ channelName: translate(topChannel.translationKey) }}
						/>
					</Text>
					<Button
						className="w-full md:w-auto"
						data-testid="signature-source-share-petition-button"
						data-qa="signature-source-share-petition-button"
						variant="secondary-solid"
						size="x-small"
						onClick={handleShareClick}
					>
						<Translate value="corgi.petition.starter_dashboard_v2.petition_insights.signature_sources.share_petition_button" />
					</Button>
				</>
			)}
		</div>
	);
}
